import {Inject, Injectable} from '@angular/core';
import {FullCustomDimensionsSet} from './full-custom-dimensions-set';
import {
  DataLayer_PT_FileDetail,
  DataLayer_PT_Home,
  DataLayer_PT_StadiumDetails,
  DataLayer_PT_StadiumDetailsCamera,
  DataLayer_PT_StoryboardDetail,
  DataLayer_PT_StoryboardDetailVP,
  DataLayer_PT_StoryboardList,
  DataLayer_PT_TopicCategory,
  DataLayer_PT_TopicDetail,
  DataLayerCustomDimensions
} from './data-layer-custom-dimensions';
import {environment} from 'environments/environment';
import {AuthService} from './auth.service';
import {Router} from '@angular/router';

export type PartialCustomDimensionsSet = { [key: string]: string };

@Injectable({
  providedIn: 'root'
})
export class GoogleTagManagerService {
  private isLoaded = false;
  private readonly gtmId: string;
  private _currentSet: FullCustomDimensionsSet;

  private browserGlobals = {
    windowRef(): any {
      return window;
    },
    documentRef(): any {
      return document;
    }
  };

  constructor(
    @Inject('googleTagManagerId') public googleTagManagerId: string,
    private authService: AuthService,
    private router: Router
  ) {
    this.gtmId = googleTagManagerId;
    this._generateInitialDimensionsSet();
    this._constantDimensions = {
      [DataLayerCustomDimensions.Event]: 'Page Meta',
      [DataLayerCustomDimensions.Language]: 'EN',
      [DataLayerCustomDimensions.PageLanguage]: 'EN',
      [DataLayerCustomDimensions.ContentSection1]: 'uptop',
      [DataLayerCustomDimensions.PageTitle]: 'UEFA UPTOP',
      [DataLayerCustomDimensions.Environment]: environment.gtm.env,
      [DataLayerCustomDimensions.UserID]: undefined,
    };
  }

  private _constantDimensions: PartialCustomDimensionsSet;

  set constantDimensions(someDimensions: PartialCustomDimensionsSet) {
    Object.keys(this._constantDimensions).map(key => DataLayerCustomDimensions[key])
      .forEach(key => {
        this._constantDimensions[key] = someDimensions[key] || this._constantDimensions[key];
      });
  }

  set dimensions(someDimensions: PartialCustomDimensionsSet) {
    Object.keys(DataLayerCustomDimensions).map(key => DataLayerCustomDimensions[key])
      .forEach(key => {
        this._currentSet[key] = someDimensions[key] || this._constantDimensions[key];
      });
  }

  public getDataLayer() {
    const window = this.browserGlobals.windowRef();
    window['dataLayer'] = window['dataLayer'] || [];
    return window['dataLayer'];
  }

  public addGtmToDom() {
    if (this.isLoaded) {
      return;
    }
    const doc = this.browserGlobals.documentRef();
    this.pushOnDataLayer({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    const gtmScript = doc.createElement('script');
    gtmScript.id = 'GTMscript';
    gtmScript.async = true;
    gtmScript.src = this.applyGtmQueryParams(
      '//www.googletagmanager.com/gtm.js'
    );
    doc.head.insertBefore(gtmScript, doc.head.firstChild);

    const ifrm = doc.createElement('iframe');
    ifrm.setAttribute(
      'src',
      this.applyGtmQueryParams('//www.googletagmanager.com/ns.html')
    );
    ifrm.style.width = '0';
    ifrm.style.height = '0';
    ifrm.style.display = 'none';
    ifrm.style.visibility = 'hidden';

    const noscript = doc.createElement('noscript');
    noscript.id = 'GTMiframe';
    noscript.appendChild(ifrm);

    doc.body.insertBefore(noscript, doc.body.firstChild);
    this.isLoaded = true;
  }

  public pushPageView(routeData) {

    let dim = {};
    const replacementObj = {};
    let requestValid = false;


    // Home page
    if (routeData.segments.length === 0) {
      dim = Object.assign({}, DataLayer_PT_Home);
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.params.id;
      replacementObj['{competition}'] = routeData.params.id;
      requestValid = true;
    }

    // Stadium details
    if (routeData.segments.length > 0 && routeData.segments[0].path === 'stadium') {
      dim = Object.assign({}, DataLayer_PT_StadiumDetails);
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.params.id;
      replacementObj['{competition}'] = routeData.params.id;
      requestValid = true;
    }

    // Storyboard details
    if (routeData.segments.length > 0 && routeData.segments[0].path === 'storyboard') {
      dim = Object.assign({}, DataLayer_PT_StoryboardDetail);
      replacementObj['{competition}'] = routeData.parentParams.id;
      replacementObj['{howtosection}'] = routeData.segments[1].path;
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.parentParams.id;
      requestValid = true;
    }

    // Storyboard list and special case of details
    if (routeData.segments.length > 0 && routeData.segments[0].path === 'storyboards') {
      // storyboard details accessed from the list
      if (routeData.segments.length > 2) {
        dim = Object.assign({}, DataLayer_PT_StoryboardDetail);
        replacementObj['{howtosection}'] = routeData.segments[2].path;
      } else {
        dim = Object.assign({}, DataLayer_PT_StoryboardList);
      }

      replacementObj['{competition}'] = routeData.parentParams.id;
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.parentParams.id;
      requestValid = true;
    }


    // files
    if (routeData.segments.length > 0 && routeData.segments[0].path === 'file') {
      dim = Object.assign({}, DataLayer_PT_FileDetail);
      replacementObj['{competition}'] = routeData.parentParams.id;
      replacementObj['{filename}'] = routeData.segments[1].path;
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.parentParams.id;
      requestValid = true;
    }

    if (routeData.segments.length > 2 && routeData.segments[0].path === 'files') {
      dim = Object.assign({}, DataLayer_PT_FileDetail);
      replacementObj['{competition}'] = routeData.parentParams.id;
      replacementObj['{filename}'] = routeData.segments[2].path;
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.parentParams.id;
      requestValid = true;
    }


    // topics category and details
    if (routeData.segments.length > 0 && routeData.segments[0].path === 'topic') {
      replacementObj['{competition}'] = routeData.parentParams.id;
      replacementObj['{topiccategory}'] = routeData.segments[1].path;
      // topic category
      if (routeData.segments.length < 4) {
        dim = Object.assign({}, DataLayer_PT_TopicCategory);
      } else {
        // topic details
        dim = Object.assign({}, DataLayer_PT_TopicDetail);
        replacementObj['{topicname}'] = routeData.segments[3].path;
      }
      dim[DataLayerCustomDimensions.ContentSection2] = routeData.parentParams.id;
      requestValid = true;
    }

    if (requestValid) {
      dim[DataLayerCustomDimensions.PageName] =
        this._convertPageName(
          dim[DataLayerCustomDimensions.PageName],
          replacementObj
        );

      if (this.isLoaded) {
        dim[DataLayerCustomDimensions.Event] = 'virtPath';
        dim[DataLayerCustomDimensions.VirtualPath] = dim[DataLayerCustomDimensions.PageName];
      }

      this.dimensions = dim;
      this.pushOnDataLayer(this._currentSet);
      if (!this.isLoaded) {
        this.addGtmToDom();
      }
    }
  }

  public pushVirtualPageView(type, id, routeData) {
    if (!this.isLoaded) {
      this.addGtmToDom();
    }

    let dim = this._currentSet;
    const replacementObj = {};

    switch (type) {
      case 'storyboard':
        dim = Object.assign(dim, DataLayer_PT_StoryboardDetailVP);
        replacementObj['{competition}'] = routeData.parentParams.id;
        replacementObj['{howtosection}'] = id;
        break;
      case 'stadium':
        dim = Object.assign(dim, DataLayer_PT_StadiumDetailsCamera);
        replacementObj['{competition}'] = routeData.parentParams.id;
    }

    dim[DataLayerCustomDimensions.PageName] =
      this._convertPageName(
        dim[DataLayerCustomDimensions.PageName],
        replacementObj
      );

    dim[DataLayerCustomDimensions.VirtualPath] =
      this._convertPageName(
        dim[DataLayerCustomDimensions.VirtualPath],
        replacementObj
      );

    this.dimensions = dim;
    this.pushOnDataLayer(this._currentSet);
  }

  public pushEvent(action) {
    // early return for admin paths
    if (this.router.url.includes('/admin')) {
      return;
    }
    const eventObject = {
      'event': 'gaEvent',
      'eventCategory': 'Click',
      'eventAction': action,
      'eventLabel': this._currentSet[DataLayerCustomDimensions.ContentSection2],
    };
    this.pushOnDataLayer(eventObject);
  }

  private pushOnDataLayer(obj: object) {
    const dataLayer = this.getDataLayer();
    dataLayer.push(obj);
  }

  private applyGtmQueryParams(url: string) {
    const params: string[] = [`id=${this.gtmId}`];

    if (url.indexOf('?') === -1) {
      url += '?';
    }

    return url + params.join('&');
  }

  private _generateInitialDimensionsSet() {
    this._currentSet = {
      [DataLayerCustomDimensions.Event]: 'Page Meta',
      [DataLayerCustomDimensions.VirtualPath]: undefined,
      [DataLayerCustomDimensions.Language]: 'EN',
      [DataLayerCustomDimensions.Country]: undefined,
      [DataLayerCustomDimensions.PageType]: undefined,
      [DataLayerCustomDimensions.PageSubType]: undefined,
      [DataLayerCustomDimensions.PageTitle]: undefined,
      [DataLayerCustomDimensions.PageName]: undefined,
      [DataLayerCustomDimensions.PageLanguage]: 'EN',
      [DataLayerCustomDimensions.ContentSection1]: 'uptop',
      [DataLayerCustomDimensions.ContentSection2]: undefined,
      [DataLayerCustomDimensions.ContentSection3]: undefined,
      [DataLayerCustomDimensions.Environment]: environment.gtm.env,
      [DataLayerCustomDimensions.UserID]: undefined,
    };
  }

  private _convertPageName(source, replacementArray): string {
    const keys = Object.keys(replacementArray);
    for (let i = 0; i < keys.length; i++) {
      const regex = new RegExp(keys[i], 'g');
      source = source.replace(regex, replacementArray[keys[i]]);
    }
    return source;
  }
}
